/**
 * `attachments.*` — the shared, polymorphic file-attachment service.
 *
 * Keyed on `(ownerType, ownerId)` so any entity reuses one flow:
 *   1. `requestUpload` → `{ uploadUrl, objectKey }` (no DB row yet).
 *   2. SPA PUTs the binary to `uploadUrl`.
 *   3. `confirm` → inserts a row + audits.
 * `list` returns active rows for an owner; `remove` is a soft delete.
 *
 * Mirrors `billAttachments.*`, generalised. Add an owner by extending the
 * `ownerType` enum (+ the domain `ATTACHMENT_OWNER_TYPES`).
 */
import { z } from "zod";
import { uuid } from "../_common.js";

/** Owner entity types that may carry attachments. Keep in sync with the
 *  domain `ATTACHMENT_OWNER_TYPES`. */
export const ownerType = z.enum(["invoice", "garment_sample_submission"]);
export type AttachmentOwnerType = z.infer<typeof ownerType>;

/** MIME allow-list — keep in sync with `ALLOWED_ATTACHMENT_TYPES` in domain. */
export const attachmentMime = z.enum(["application/pdf", "image/jpeg", "image/png", "image/webp"]);
export type AttachmentMime = z.infer<typeof attachmentMime>;

/** 25 MB — keep in sync with `ATTACHMENT_MAX_BYTES` in domain. */
const MAX_BYTES = 25 * 1024 * 1024;

export const requestUploadInput = z.object({
  ownerType,
  ownerId: uuid,
  originalName: z.string().trim().min(1, "Filename is required").max(255, "Filename is too long"),
  contentType: attachmentMime,
  sizeBytes: z.number().int().min(1, "File is empty").max(MAX_BYTES, `Max ${MAX_BYTES} bytes (~25 MB)`),
});

export const requestUploadOutput = z.object({
  uploadUrl: z.string().url(),
  objectKey: z.string(),
  contentType: attachmentMime,
  maxBytes: z.number().int(),
});

export const confirmInput = z.object({
  ownerType,
  ownerId: uuid,
  objectKey: z.string().min(1),
  originalName: z.string().trim().min(1).max(255),
  contentType: attachmentMime,
  sizeBytes: z.number().int().min(1).max(MAX_BYTES),
});

export const confirmOutput = z.object({
  id: uuid,
  storageKey: z.string(),
});

export const listInput = z.object({
  ownerType,
  ownerId: uuid,
});

export const listRow = z.object({
  id: uuid,
  ownerType: z.string(),
  ownerId: uuid,
  originalName: z.string(),
  storageKey: z.string(),
  contentType: z.string(),
  /** Stringified bigint so JSON round-trips don't lose precision. */
  sizeBytes: z.string(),
  status: z.enum(["attached", "removed"]),
  uploadedAt: z.string().datetime(),
  /** Public download/view URL when one can be derived, else `null`. */
  publicUrl: z.string().url().nullable(),
});

export const listOutput = z.object({
  items: z.array(listRow),
});

export const removeInput = z.object({
  id: uuid,
});

export const removeOutput = z.object({
  id: uuid,
  removedAt: z.string().datetime(),
});
